import { apiGet, apiPost } from './client';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DomainCount {
  domain: string;
  count: number;
}

export interface AutoArchiveCandidate {
  domain: string;
  deletedCount: number;
}

export interface SenderClassEntry {
  domain: string;
  class: string;
  messageCount: number;
  unreadCount: number;
  oldestUnreadDays: number;
}

export interface ClassBreakdownEntry {
  messageCount: number;
  domainCount: number;
}

export interface DailySeries {
  date: string;
  received: number;
  sent: number;
}

export interface AnalysisMetrics {
  // Volume
  totalInbox90d: number;
  daysWithData: number;
  minPerDay: number;
  maxPerDay: number;
  meanPerDay: number;
  medianPerDay: number;
  p90PerDay: number;
  // Sent
  totalSent90d: number;
  meanSentPerDay: number;
  sentToReceivedRatio: number;
  // Long-term unread
  unreadOver30d: number;
  unreadOver90d: number;
  unreadOver180d: number;
  unreadOver30dPct: number;
  // Inbox structure
  inboxTotalItems: number;
  inboxUnreadItems: number;
  inboxChildFolderCount: number;
  inboxOldestDate: string | null;
  subfolders: Array<{ name: string; totalItems: number; unreadItems: number }>;
  // Archive
  archiveExists: boolean;
  archiveTotalItems: number;
  archiveChildFolderCount: number;
  isActiveArchiver: boolean;
  // Folder totals
  sentTotalItems: number;
  deletedTotalItems: number;
  junkTotalItems: number;
  // Sender profile
  uniqueExternalDomains: number;
  top20Domains: DomainCount[];
  bulkAutomatedPct: number;
  internalPct: number;
  // Sender classification
  senderClassification: SenderClassEntry[];
  classBreakdown: Record<string, ClassBreakdownEntry>;
  externalGenuineDomains: string[];
  // Thread complexity
  uniqueThreads: number;
  meanMsgsPerThread: number;
  singleMsgThreadPct: number;
  threadsOver10: number;
  threadsOver20: number;
  threadsWithReply: number;
  replyRatePct: number;
  // Content flags
  unreadPct: number;
  withAttachmentPct: number;
  // Deleted items
  deletedTop20Domains: DomainCount[];
  deletedBulkPct: number;
  autoArchiveCandidates: AutoArchiveCandidate[];
  // Daily series
  dailySeries: DailySeries[];
}

export type AnalysisStatus =
  | 'none'
  | 'pending'
  | 'running_metrics'
  | 'running_narrative'
  | 'complete'
  | 'error';

export interface AnalysisResult {
  status: AnalysisStatus;
  analysisId?: string;
  triggeredBy?: string;
  createdAt?: string;
  updatedAt?: string;
  metrics?: AnalysisMetrics;
  metricsComputedAt?: string;
  messageCount?: number;
  daysAnalysed?: number;
  narrative?: string;
  narrativeComputedAt?: string;
  errorMessage?: string;
}

// ── API functions ─────────────────────────────────────────────────────────────

export async function getAnalysisLatest(): Promise<AnalysisResult> {
  return apiGet<AnalysisResult>('/api/analysis/latest');
}

export async function runAnalysis(): Promise<{ analysisId: string; status: string }> {
  return apiPost<{ analysisId: string; status: string }>('/api/analysis/run');
}
